/* global loadConfigData,loadFeatureFlags,require */

// ARH 2020-06: This file initializes the load process by setting up all
// the config listeners, loading data that index.xqy preloaded into a
// JavaScript element, and triggering the actual load of the base site code.

import Backbone from 'backbone';
import Radio from 'backbone.radio';

window.Backbone = Backbone;
window.Backbone.Radio = Radio;


import {
    FeatureFlagManager,
    ServerConfigManager
} from './lib/preload/ServerConfigManager';

// We could use the "load" Radio channel, but we want to import this module
// so that we guarantee the events are available on that channel.
import {
    loadCSS,
    loadPrimaryFonts,
    loadSupplementalFonts, loadTemplates
} from './lib/preload/AssetLoadingHelpers';


// Ensure the function defined in `/sjs_server/index.xqy` is present here
if (typeof window.loadConfigData !== 'function')
{
    throw new Error('loader.js: loadConfigData function not available!');
}


// Grab references to what we'll need later
const configChannel = Radio.channel('config');


// If we're running tests, there'll be a variable imported from /test/config/test-config to tell us
// where to actually load things from.
window.root = window.testRoot || window.location.origin;


// Unpack the server config data and delete the element it was stored in (for cleanliness)
const configData = loadConfigData();
const sm = new ServerConfigManager(configData);

const featureFlags = loadFeatureFlags();
const fm = new FeatureFlagManager(featureFlags);

// Make sure the primary fonts are loading. Hopefully this will have started
// already with "preload." Use "void" because we really don't need the
// Promise returned here.
void loadPrimaryFonts();


// Load the replacement for the Marionette template cache. Use require()
// to ensure it's loaded immediately.
require('./lib/preload/MarionetteTemplateCache.js');


// Unpack the API address now that it's set
window.api_address = `${window.root}/${configChannel.request('property', 'siteIDPrefix')}`;  // eslint-disable-line camelcase


// Start the dots blinking
const ellipsisInterval = window.setInterval(
    function ()
    {
        const dots = document.querySelectorAll('.loading-dot');
        const inactiveLength = document.querySelectorAll('.loading-dot.inactive').length;

        if (inactiveLength === 0)
        {
            for (let dIdx = 1; dIdx < dots.length; dIdx++)
            {
                dots[dIdx].className = 'loading-dot inactive';
            }
        }
        else
        {
            dots[dots.length - inactiveLength].className = 'loading-dot';
        }
    },
    500
);

const preloadTimeout = window.setTimeout(
    function ()
    {
        const preloadTextElem = document.getElementById('preload-text');
        if (preloadTextElem)
        {
            preloadTextElem.textContent = 'The site is taking a while to load. You may want to try reloading the page, or you can keep waiting';
        }
    },
    20000
);


// Trigger the primary load and clear intervals when we're done
window.loadComplete = (async function ()
{
    // First load the TEI base CSS. This must happen before any other CSS
    // is loaded because of legacy reasons.
    await loadCSS('/css/tei/tei_html.css');

    // Load the Egret base app.
    const stylesAndTemplates = Promise.all([
        loadCSS(`/css/sites/${configChannel.request('property', 'siteIDPrefix').toUpperCase()}/main.css`),
        loadTemplates(`/templates/main-${configChannel.request('property', 'siteIDPrefix')}.json`)
    ]);

    const clientModule = await import('./EgretClient');
    await stylesAndTemplates;
    await clientModule.init();

    window.clearInterval(ellipsisInterval);
    window.clearTimeout(preloadTimeout);

    // Load less important fonts.
    await loadSupplementalFonts();
})().catch((err) =>
{
    window.clearInterval(ellipsisInterval);
    console.error(err);  // eslint-disable-line no-console

    // Show a default error to the user
    const statusEl = document.querySelector('.preload-status');
    if (statusEl)
    {
        statusEl.innerHTML = 'The site is currently unavailable due to scheduled downtime or upgrades; please check back in five minutes.';
    }
});


// Add a few browser recognition classes - https://stackoverflow.com/questions/9847580
const appEl = document.getElementById('app');

// Internet Explorer 6-11
if (/* @cc_on!@*/false || !!document.documentMode)
{
    appEl.classList.add('is-IE');
}

// Safari 3.0+ "[object HTMLElementConstructor]"
if ((/constructor/i).test(window.HTMLElement) || (function (p) {return p.toString() === '[object SafariRemoteNotification]';})(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification)))
{
    appEl.classList.add('is-Safari');
}

// any iPhone or iPod Touch browser
if ((/(iPhone)/).test((navigator && navigator.userAgent) || ''))
{
    appEl.classList.add('is-iOS');
}
